import React, { FC, ReactNode, useEffect } from 'react';

import { useTermsContext } from '~/context/GlossaryTermsProvider';
import useContentAvailability from '~/hooks/useContentAvailability';
import { buildGlossaryRoute } from '~/routes';
import { GlossaryTerm } from '~/types';
import Term from '~/ui/components/TextWithTerms/Term';
import useHash from '~/ui/components/TextWithTerms/useHash';
import toChunksByTerms from '~/ui/components/TextWithTerms/utils/toChunksByTerms';

interface StringWithTermsProps {
  termsById: Record<string, GlossaryTerm[]>;
  parentId?: string;
  children?: ReactNode;
}

const StringWithTerms: FC<StringWithTermsProps> = ({ children, parentId, termsById }) => {
  const { addTextEntry, removeTextEntry } = useTermsContext();
  const canIncludeGlossaryTerm = children && typeof children === 'string';
  const id = useHash(canIncludeGlossaryTerm ? children : null);
  const componentId = parentId && id ? [parentId, id].join('_') : null;
  const { glossary: glossaryAvailable } = useContentAvailability();

  useEffect(() => {
    if (!glossaryAvailable || !canIncludeGlossaryTerm || !componentId) {
      return;
    }

    const text = children.trim();

    if (text) {
      addTextEntry(componentId, text);

      return () => removeTextEntry(componentId);
    }
  }, [
    addTextEntry,
    componentId,
    children,
    removeTextEntry,
    canIncludeGlossaryTerm,
    glossaryAvailable,
  ]);

  const terms = componentId
    ? [...(termsById[componentId] || [])]?.sort((a, b) => Number(a.position) - Number(b.position))
    : null;

  if (!canIncludeGlossaryTerm || !terms) {
    return children;
  }

  const chunks = toChunksByTerms({
    terms,
    fullString: children,
  });

  return (
    <>
      {chunks.map((chunk, index) => {
        if (chunk.type === 'text') {
          return chunk.content;
        }

        return (
          <Term
            key={index}
            title={chunk.term.title}
            definition={chunk.term.definition}
            link={buildGlossaryRoute(chunk.term.slug)}
          >
            {chunk.content}
          </Term>
        );
      })}
    </>
  );
};

export default StringWithTerms;
